import * as THREE from 'three';

import type { PlayerState } from '../core/player.ts';
import type { SimEvent } from '../core/sim.ts';

/**
 * Damage and healing, written in the air where it happened.
 *
 * This exists so the combat log does not have to carry it. A number over the
 * mage you just hit says what a line of scrolling text says, in the place you
 * were already looking, and it frees the log for the things you genuinely
 * cannot see — deaths, levels, objectives, and damage arriving from behind you.
 *
 * Three rules shape it, and all three came out of playtesting the alternative:
 *
 * **Yours only.** A three-way fight with everybody's numbers on screen is
 * unreadable, and worse, it would draw numbers over mages you have no line of
 * sight to.
 *
 * **Accumulated per target.** A tier V Bleeding is eight ticks and a burn is up
 * to ten; two Clerics working one mage could put thirty numbers in the air. One
 * number climbing from 4 to 34 says more than eight separate 4s, and keeps the
 * burst damage that actually matters from drowning in drizzle.
 *
 * **Occluded.** These are sprites with depth testing, so a number over somebody
 * who has run behind a pillar is hidden with them. A bleed ticking away above a
 * wall would be free reconnaissance for eight seconds — the same reason there
 * are no enemy nameplates.
 */

/** How long a number lives before it has faded out entirely. */
const LIFETIME = 1.15;
/**
 * How long a number keeps taking additions.
 *
 * Longer than a bleed's one-second tick, so a running total keeps running rather
 * than restarting halfway through.
 */
const ACCUMULATE_FOR = 1.4;
/** Metres risen over a lifetime. Enough to read as rising, not as launched. */
const RISE = 0.85;
/**
 * The first fraction of a second is drawn through geometry.
 *
 * You had line of sight to land the hit, so being told what it did is not
 * telling you anything you did not have — and a blast that goes off behind cover
 * should still report itself. After that the number is occluded like anything
 * else.
 */
const GRACE = 0.3;

/** Deliberately small. Enough to read, not enough to fight the spell that caused it. */
const SIZE_NORMAL = 0.3;
const SIZE_HEADSHOT = 0.42;

const COLOUR_NORMAL = '#ff9d4a';
const COLOUR_HEADSHOT = '#ff4a3c';
const COLOUR_HEAL = '#5fd97a';

type Style = 'normal' | 'headshot' | 'heal';

interface Floater {
  sprite: THREE.Sprite;
  /** Whose head it is over, so further damage finds it. */
  targetId: string | null;
  total: number;
  style: Style;
  life: number;
  /** Seconds since it appeared, for the grace window and the drift. */
  age: number;
  drift: number;
  base: THREE.Vector3;
}

export class DamageNumbers {
  private readonly scene: THREE.Scene;
  private readonly pool: Floater[] = [];
  /** The live number for each target, so ticks add rather than pile up. */
  private readonly byTarget = new Map<string, Floater>();
  /** Rendered text, cached because most values recur within a match. */
  private readonly textures = new Map<string, THREE.CanvasTexture>();

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  /**
   * A number as a texture.
   *
   * Cached by what it says: a fight produces the same handful of values over and
   * over, and rasterising text is far more expensive than looking it up.
   */
  private texture(text: string, style: Style): THREE.CanvasTexture {
    const key = `${text}|${style}`;
    const cached = this.textures.get(key);
    if (cached) return cached;

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) throw new Error('no 2d context for a damage number');

    const height = 64;
    const font = `700 ${height * 0.66}px "Inter", system-ui, sans-serif`;
    context.font = font;
    canvas.width = Math.ceil(context.measureText(text).width) + 20;
    canvas.height = height;

    // Re-set: resizing a canvas resets its context.
    context.font = font;
    context.textAlign = 'center';
    context.textBaseline = 'middle';

    // Outlined rather than plated. A box would read as an interface element
    // sitting in the world; an outline just keeps the digits legible against
    // whatever they happen to be in front of.
    context.lineWidth = 7;
    context.strokeStyle = 'rgba(0, 0, 0, 0.9)';
    context.strokeText(text, canvas.width / 2, height / 2);

    context.fillStyle = style === 'heal' ? COLOUR_HEAL
      : style === 'headshot' ? COLOUR_HEADSHOT : COLOUR_NORMAL;
    context.fillText(text, canvas.width / 2, height / 2);

    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    this.textures.set(key, texture);
    return texture;
  }

  /**
   * A sprite to write on, reused.
   *
   * Pooled because each needs its own material — they fade independently — and
   * allocating a material per hit is the allocation churn that shows up as a
   * collection pause in the middle of a fight.
   */
  private take(): Floater {
    for (const floater of this.pool) {
      if (floater.life <= 0) return floater;
    }

    const sprite = new THREE.Sprite(new THREE.SpriteMaterial({
      transparent: true,
      depthWrite: false,
      // Off for the grace window, then on. Not part of a program's cache key, so
      // toggling it costs nothing.
      depthTest: false,
    }));
    sprite.visible = false;
    this.scene.add(sprite);

    const floater: Floater = {
      sprite, targetId: null, total: 0, style: 'normal',
      life: 0, age: 0, drift: 0, base: new THREE.Vector3(),
    };
    this.pool.push(floater);
    return floater;
  }

  /**
   * Rasterises the numbers a fight will ask for, and draws one, during loading.
   *
   * Two separate costs hide behind the first damage number of a match, and the
   * warm-up in `actors.warmUp` reached neither because no sprite exists in the
   * scene until somebody is hit.
   *
   * The first is the **program**. A `Sprite` is not a mesh: `SpriteMaterial`
   * compiles its own shader, and nothing else in the game draws one except the
   * nameplates. Whichever of the two appears first pays for it.
   *
   * The second is the **canvas**. Every distinct value is a `createElement`, a
   * font shaping pass, a rasterisation and a texture upload on first draw — and
   * they are not amortised, because a fight producing 7, 12, 31 and 44 wants
   * four of them. Pre-rendering the range means the cache always hits.
   *
   * The range is deliberately generous rather than measured. A canvas this small
   * costs a fraction of a millisecond, the loading screen has the time, and a
   * value that falls outside is simply built the old way — this makes the common
   * case free, it does not have to be exhaustive.
   */
  warmUp(at: THREE.Vector3): { sprite: THREE.Sprite | null; textures: number } {
    /*
     * Nothing to warm without a DOM.
     *
     * The tests build an `Actors` against a bare `THREE.Scene` under Node to
     * check what the warm-up puts in the scene, and there is no `document` there
     * to rasterise onto. Everything this method exists to avoid is a browser
     * cost, so skipping it headless removes no coverage — the assertions those
     * tests make are about meshes, which are built either way.
     */
    if (typeof document === 'undefined') return { sprite: null, textures: 0 };

    for (let n = 1; n <= 200; n++) {
      this.texture(String(n), 'normal');
      this.texture(String(n), 'headshot');
      this.texture(`+${n}`, 'heal');
    }

    /*
     * Drawn where the camera can see it, not parked under the map.
     *
     * A sprite is billboarded and depth-tested but it is still submitted from
     * wherever it stands, and the point of the exercise is to get one through a
     * real draw call so the program is built and one texture is uploaded. In
     * front of the camera is inside the frustum for certain; frustum culling is
     * turned off anyway, for the same reason the rest of the warm objects do.
     */
    const floater = this.take();
    floater.total = 1;
    floater.style = 'normal';
    floater.life = 0;
    floater.sprite.position.copy(at);
    floater.sprite.visible = true;
    floater.sprite.frustumCulled = false;
    this.draw(floater);

    return { sprite: floater.sprite, textures: this.textures.size };
  }

  /** Puts the warm sprite away. The program and the textures stay. */
  coolDown(): void {
    for (const floater of this.pool) {
      floater.sprite.visible = false;
      floater.life = 0;
    }
  }

  private draw(floater: Floater): void {
    const text = floater.style === 'heal' ? `+${floater.total}` : String(floater.total);
    const texture = this.texture(text, floater.style);

    floater.sprite.material.map = texture;
    floater.sprite.material.needsUpdate = true;

    // Scaled from the texture, so three digits are wider rather than squashed.
    const size = floater.style === 'headshot' ? SIZE_HEADSHOT : SIZE_NORMAL;
    const aspect = texture.image.width / texture.image.height;
    floater.sprite.scale.set(size * aspect, size, 1);
  }

  /**
   * Reads the frame's events and writes the ones the player caused.
   *
   * A heal of zero is not drawn at all: healing somebody already whole mends
   * nothing, and a green `+0` over their head is a lie about what just happened.
   */
  ingest(
    events: readonly SimEvent[], players: readonly PlayerState[], localId: string,
  ): void {
    // A headshot arrives beside its hit rather than instead of it, so the hits
    // it applies to have to be known before any of them are drawn.
    const headshots = new Set<string>();
    for (const ev of events) {
      if (ev.type === 'headshot' && ev.sourceId === localId) headshots.add(ev.targetId);
    }

    for (const ev of events) {
      if (ev.type !== 'hit' && ev.type !== 'heal') continue;
      if (ev.sourceId !== localId) continue;
      if (ev.amount <= 0) continue;

      const target = players.find((p) => p.id === ev.targetId);
      if (!target) continue;

      const style: Style = ev.type === 'heal' ? 'heal'
        : headshots.has(ev.targetId) ? 'headshot' : 'normal';
      const drizzle = ev.type === 'hit' && ev.overTime === true;
      this.add(ev.targetId, ev.amount, style, target, drizzle);
    }
  }

  private add(
    targetId: string, amount: number, style: Style, target: PlayerState, drizzle = false,
  ): void {
    const running = this.byTarget.get(targetId);

    /*
     * Only drizzle accumulates. A blow gets its own number.
     *
     * The rule was written for burns and bleeds — one figure climbing from 4 to
     * 34 rather than eight separate 4s — and then applied to everything, because
     * over-time damage emitted no event and impacts were the only thing that
     * ever reached it. So it did the opposite of what it says: every *blow* was
     * merged and every tick was silent.
     *
     * Which mattered most for a Wounding Triad. Three axes landing together are
     * three hits, and the whole point of a spread is that you can land two of
     * three — a single merged number cannot tell you which happened.
     */
    if (
      drizzle && running && running.life > 0
      && running.age < ACCUMULATE_FOR && running.style === style
    ) {
      running.total += amount;
      // Life reset, but not age: the grace window and the drift belong to when
      // the number first appeared.
      running.life = LIFETIME;
      running.base.set(target.pos.x, target.pos.y + 1.5, target.pos.z);
      this.draw(running);
      return;
    }

    const floater = this.take();
    floater.targetId = targetId;
    floater.total = amount;
    floater.style = style;
    floater.life = LIFETIME;
    floater.age = 0;
    // A little sideways, so two numbers landing at once do not sit on top of
    // each other and become one unreadable smudge.
    floater.drift = (Math.random() - 0.5) * 0.45;
    floater.base.set(target.pos.x, target.pos.y + 1.5, target.pos.z);
    floater.sprite.visible = true;
    this.draw(floater);
    this.byTarget.set(targetId, floater);
  }

  tick(dt: number): void {
    for (const floater of this.pool) {
      if (floater.life <= 0) continue;

      floater.life -= dt;
      floater.age += dt;

      if (floater.life <= 0) {
        floater.sprite.visible = false;
        if (floater.targetId && this.byTarget.get(floater.targetId) === floater) {
          this.byTarget.delete(floater.targetId);
        }
        continue;
      }

      const done = 1 - floater.life / LIFETIME;
      floater.sprite.position.set(
        floater.base.x + floater.drift * done,
        floater.base.y + RISE * done,
        floater.base.z,
      );

      // Held at full for the first half, then faded. A number that starts fading
      // immediately reads as already leaving.
      floater.sprite.material.opacity = done < 0.5 ? 1 : 1 - (done - 0.5) * 2;
      floater.sprite.material.depthTest = floater.age > GRACE;
    }
  }

  dispose(): void {
    for (const floater of this.pool) {
      this.scene.remove(floater.sprite);
      floater.sprite.material.dispose();
    }
    for (const texture of this.textures.values()) texture.dispose();
    this.pool.length = 0;
    this.byTarget.clear();
    this.textures.clear();
  }
}
